import { useState, useEffect } from "react";
import { collection, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, ENTITY_TYPES } from "../../config/constants";
import { db } from "../../firebase";
import { getDefaultTaxYear } from "../../utils/helpers";
import { tp, btnP, btnS } from "../../utils/styles";

export default function BusinessesTab({ client, theme, allClients, onUpdate }) {
  const t = THEMES[theme];
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editBiz, setEditBiz] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(null); // bizId being reassigned
  const [reassignTarget, setReassignTarget] = useState("");
  const [reassignSearch, setReassignSearch] = useState("");
  const [form, setForm] = useState({ legalName:"", ein:"", entityType:"LLC", industry:"", address:"", city:"", state:"", zip:"", yearFounded:"", isPrimary:false, isBookkeeping:false, status:"Not Started", assignedTo:client?.assignedTo||"Bibek" });

  // Load businesses for this client (filter client-side to avoid index requirements)
  useEffect(() => {
    if (!client?.id) { setLoading(false); return; }
    const unsub = onSnapshot(collection(db, "businesses"), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBusinesses(all.filter(b => b.ownerId === client.id));
      setLoading(false);
    });
    return unsub;
  }, [client.id]);

  function resetForm() {
    setForm({ legalName:"", ein:"", entityType:"LLC", industry:"", address:"", city:"", state:"", zip:"", yearFounded:"", isPrimary:false, isBookkeeping:false });
    setEditBiz(null);
    setShowAdd(false);
  }

  async function handleSave() {
    if (!form.legalName.trim()) { alert("Business name required"); return; }
    setSaving(true);
    try {
      if (editBiz) {
        const editId = typeof editBiz?.id === "string" ? editBiz.id : String(editBiz?.id || "");
        await updateDoc(doc(db, "businesses", editId), { ...form, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "businesses"), {
          ...form,
          ownerId: client.id,
          ownerName: client.name,
          status: form.status || "Not Started",
          assignedTo: form.assignedTo || client.assignedTo || "Bibek",
          taxYear: form.taxYear || client.taxYear || getDefaultTaxYear(),
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
    } catch(e) { alert("Save failed: " + e.message); }
    setSaving(false);
  }

  async function handleDelete(biz) {
    if (!window.confirm(`Remove "${biz.legalName}" permanently?`)) return;
    const bizId = typeof biz?.id === "string" ? biz.id : String(biz?.id || "");
    if (!bizId) { alert("Business ID missing"); return; }
    try {
      await deleteDoc(doc(db, "businesses", bizId));
    } catch(e) { alert("Delete failed: " + e.message); }
  }

  async function handleReassign(biz) {
    if (!reassignTarget) { alert("Select a client to reassign to"); return; }
    const bizId = typeof biz?.id === "string" ? biz.id : String(biz?.id || "");
    if (!bizId) { alert("Business ID missing — try refreshing"); return; }
    const target = allClients.find(c => c.id === reassignTarget);
    if (!target || !target.id) { alert("Target client not found"); return; }
    try {
      const bizRef = doc(db, "businesses", bizId);
      await updateDoc(bizRef, {
        ownerId: String(target.id),
        ownerName: String(target.name || ""),
      });
      setReassigning(null);
      setReassignTarget("");
    } catch(e) { alert("Reassign failed: " + e.message); }
  }

  function startEdit(biz) {
    setForm({ legalName:biz.legalName||"", ein:biz.ein||"", entityType:biz.entityType||"LLC", industry:biz.industry||"", address:biz.address||"", city:biz.city||"", state:biz.state||"", zip:biz.zip||"", yearFounded:biz.yearFounded||"", isPrimary:biz.isPrimary||false, isBookkeeping:biz.isBookkeeping||false });
    setEditBiz(biz);
    setShowAdd(true);
  }

  const inp = { background:t.inputBg||t.bg, border:`1px solid ${t.border}`, borderRadius:8, padding:"8px 10px", color:t.text, fontSize:13, outline:"none", fontFamily:"'Outfit',sans-serif", width:"100%", boxSizing:"border-box" };
  const F = ({ label, k, placeholder, half }) => (
    <div style={{ flex: half ? "0 0 calc(50% - 5px)" : 1, minWidth: 0 }}>
      <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{label}</div>
      <input style={inp} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={placeholder||""}/>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:13, color:t.textSub }}>{businesses.length} business{businesses.length!==1?"es":""} linked to {client.name}</div>
        {!showAdd && (
          <button onClick={()=>setShowAdd(true)} style={{...btnP, padding:"7px 14px", fontSize:12}}>
            <Icon name="plus" size={12} color="#fff"/>Add Business
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {showAdd && (
        <div style={{ background:t.bg, border:`1px solid ${BRAND.blue}40`, borderRadius:14, padding:20, marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:16 }}>
            {editBiz ? `Edit — ${editBiz.legalName}` : "Add New Business"}
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
            <F label="Legal Business Name" k="legalName" placeholder="ABC LLC"/>
            <F label="EIN" k="ein" placeholder="XX-XXXXXXX" half/>
          </div>
          <div style={{ display:"flex", gap:10, marginBottom:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Entity Type</div>
              <select style={inp} value={form.entityType} onChange={e=>setForm(p=>({...p,entityType:e.target.value}))}>
                {ENTITY_TYPES.map(x=><option key={x}>{x}</option>)}
              </select>
            </div>
            <F label="Industry" k="industry" placeholder="e.g. Consulting"/>
            <F label="Year Founded" k="yearFounded" placeholder="2018" half/>
          </div>
          <div style={{ marginBottom:10 }}><F label="Business Address" k="address" placeholder="Street address"/></div>
          <div style={{ display:"flex", gap:10, marginBottom:14 }}>
            <F label="City" k="city" placeholder="San Diego"/>
            <F label="State" k="state" placeholder="CA" half/>
            <F label="ZIP" k="zip" placeholder="92101" half/>
          </div>
          <div style={{ display:"flex", gap:20, marginBottom:16 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:t.textSub, cursor:"pointer" }}>
              <input type="checkbox" checked={!!form.isPrimary} onChange={e=>setForm(p=>({...p,isPrimary:e.target.checked}))} style={{ accentColor:BRAND.blue }}/>
              <span>Primary business</span>
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:t.textSub, cursor:"pointer" }}>
              <input type="checkbox" checked={!!form.isBookkeeping} onChange={e=>setForm(p=>({...p,isBookkeeping:e.target.checked}))} style={{ accentColor:BRAND.green }}/>
              <span style={{ color:form.isBookkeeping?BRAND.green:t.textSub }}>📒 Bookkeeping client</span>
            </label>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleSave} disabled={saving} style={{...btnP, padding:"8px 20px", fontSize:13}}>
              {saving ? <><MiniSpinner size={12} color="#fff"/>Saving...</> : editBiz ? "Save Changes" : "Add Business"}
            </button>
            <button onClick={resetForm} style={{...btnS(t), padding:"8px 16px", fontSize:13}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Business list */}
      {loading ? (
        <div style={{ display:"flex", alignItems:"center", gap:10, color:t.textMuted, padding:20 }}>
          <MiniSpinner size={14} color={BRAND.blue}/> Loading...
        </div>
      ) : businesses.length === 0 ? (
        <div style={{ textAlign:"center", padding:40, color:t.textMuted, border:`2px dashed ${t.border}`, borderRadius:12 }}>
          <div style={{ fontSize:32, marginBottom:10 }}>🏢</div>
          <div style={{ fontWeight:700, marginBottom:6 }}>No businesses linked</div>
          <div style={{ fontSize:12.5 }}>Click "Add Business" to link one.</div>
          <div style={{ fontSize:10, color:t.textMuted, marginTop:8 }}>Client ID: {client?.id||"none"}</div>
        </div>
      ) : (
        businesses.sort((a,b)=>(b.isPrimary?1:0)-(a.isPrimary?1:0)).map(biz => (
          <div key={biz.id} style={{ background:t.bg, borderRadius:12, padding:18, border:`1px solid ${biz.isPrimary?BRAND.blue+"60":t.border}`, borderLeft:`4px solid ${biz.isPrimary?BRAND.blue:"transparent"}`, marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:4 }}>{biz.legalName||"Unnamed Business"}</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {biz.isPrimary && <span style={{ fontSize:10, color:BRAND.blue, fontWeight:700, background:BRAND.blue+"18", padding:"2px 8px", borderRadius:8 }}>Primary</span>}
                  {biz.isBookkeeping && <span style={{ fontSize:10, color:BRAND.green, fontWeight:700, background:BRAND.green+"18", padding:"2px 8px", borderRadius:8 }}>📒 Bookkeeping</span>}
                  <span style={tp("Business",theme)}>{biz.entityType}</span>
                  {biz.industry && <span style={{ fontSize:11, color:t.textMuted }}>{biz.industry}</span>}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>startEdit(biz)} style={{ padding:"5px 10px", borderRadius:7, background:BRAND.blue+"15", border:`1px solid ${BRAND.blue}30`, color:BRAND.blue, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"'Outfit',sans-serif" }}>Edit</button>
                <button onClick={()=>{setReassigning(reassigning===biz.id?null:biz.id);setReassignSearch("");setReassignTarget("");}} style={{ padding:"5px 10px", borderRadius:7, background:"#f59e0b15", border:"1px solid #f59e0b30", color:"#f59e0b", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"'Outfit',sans-serif" }}>Reassign</button>
                <button onClick={()=>handleDelete(biz)} style={{ padding:"5px 8px", borderRadius:7, background:"#f8717115", border:"1px solid #f8717130", color:"#f87171", fontSize:11, cursor:"pointer", fontFamily:"'Outfit',sans-serif" }}><Icon name="trash" size={12} color="#f87171"/></button>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom: reassigning===biz.id ? 14 : 0 }}>
              {[{l:"EIN",v:biz.ein},{l:"Year Founded",v:biz.yearFounded},{l:"City",v:biz.city},{l:"State",v:biz.state},{l:"ZIP",v:biz.zip}].map(f=>(
                <div key={f.l}>
                  <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:2 }}>{f.l}</div>
                  <div style={{ fontSize:12.5, color:t.text, fontWeight:500 }}>{f.v||"—"}</div>
                </div>
              ))}
              {biz.address && (
                <div style={{ gridColumn:"span 3" }}>
                  <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:2 }}>Address</div>
                  <div style={{ fontSize:12.5, color:t.text }}>{[biz.address,biz.city,biz.state,biz.zip].filter(Boolean).join(", ")}</div>
                </div>
              )}
            </div>

            {/* Reassign panel */}
            {reassigning===biz.id && (()=>{
              const filteredClients = allClients
                .filter(c=>c.id!==client.id)
                .filter(c=>!reassignSearch||c.name.toLowerCase().includes(reassignSearch.toLowerCase()))
                .sort((a,b)=>a.name.localeCompare(b.name));
              const selectedClient = allClients.find(c=>c.id===reassignTarget);
              return (
                <div style={{ marginTop:12, padding:"14px 16px", background:theme==="dark"?"#1a1200":"#fffbeb", border:"1px solid #f59e0b40", borderRadius:10 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:"#f59e0b", marginBottom:10 }}>Reassign "{biz.legalName}" to another client</div>
                  {/* Search input */}
                  <div style={{ position:"relative", marginBottom:8 }}>
                    <input
                      autoFocus
                      value={reassignSearch}
                      onChange={e=>{setReassignSearch(e.target.value);setReassignTarget("");}}
                      placeholder="Search client by name..."
                      style={{...inp, paddingLeft:32}}
                    />
                    <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:t.textMuted, fontSize:13 }}>🔍</span>
                    {reassignSearch && (
                      <button onClick={()=>{setReassignSearch("");setReassignTarget("");}}
                        style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:t.textMuted, fontSize:16, lineHeight:1, padding:2 }}>×</button>
                    )}
                  </div>
                  {/* Results list */}
                  {filteredClients.length > 0 ? (
                    <div style={{ maxHeight:180, overflowY:"auto", border:`1px solid ${t.border}`, borderRadius:8, marginBottom:10 }}>
                      {filteredClients.map(c=>(
                        <div key={c.id} onClick={()=>{setReassignTarget(c.id);setReassignSearch(c.name);}}
                          style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, color:t.text, fontWeight:reassignTarget===c.id?700:400,
                            background:reassignTarget===c.id?("#f59e0b"+"18"):t.bg,
                            borderBottom:`1px solid ${t.borderLight||t.border}`,
                            display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <span>{c.name}</span>
                          {reassignTarget===c.id && <span style={{ fontSize:11, color:"#f59e0b", fontWeight:700 }}>✓ Selected</span>}
                        </div>
                      ))}
                    </div>
                  ) : reassignSearch ? (
                    <div style={{ padding:"10px 12px", fontSize:12, color:t.textMuted, fontStyle:"italic" }}>No clients found for "{reassignSearch}"</div>
                  ) : null}
                  {/* Selected + action buttons */}
                  {selectedClient && (
                    <div style={{ fontSize:12, color:"#f59e0b", marginBottom:10, fontWeight:600 }}>
                      → Reassigning to: <strong>{selectedClient.name}</strong>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={()=>handleReassign(biz)} disabled={!reassignTarget}
                      style={{ padding:"8px 18px", borderRadius:8, background:reassignTarget?"#f59e0b":"#ccc", border:"none", color:"#fff", fontSize:12, fontWeight:700, cursor:reassignTarget?"pointer":"not-allowed", fontFamily:"'Outfit',sans-serif" }}>
                      Reassign
                    </button>
                    <button onClick={()=>{setReassigning(null);setReassignTarget("");setReassignSearch("");}}
                      style={{...btnS(t), padding:"8px 12px", fontSize:12}}>Cancel</button>
                  </div>
                </div>
              );
            })()}
          </div>
        ))
      )}
    </div>
  );
}

